import React, { useEffect, useState } from 'react';

import 'video-react/dist/video-react.css';
import { Event, Tag, TagState, Tags } from '../../../interfaces';
import { useEventTags, useTagsQuery } from '../../../queries';
import { humanizeEnum, tagDescription } from '../../../utils';
import { FilterToggleWrapper, HelpIcon, SpaceBetween, Spacer, ToggleButton } from '../../atoms';
import { handleButtonClick, handleDropdownClick, handleTagClick } from '../../reviewGridMatchedEvents/utils';
import { Autocomplete } from './Autocomplete';
import { Question } from './Question';
import { useHasPermission } from '../../../hooks';

interface Props {
  event?: Event;
  tags?: Tags; // supply this to make this a controlled component
  hideButtons?: boolean;
  onUserTagChange?: (tags: Tags) => void;
  onTagsLoad?: (tags: Tags) => void;
  withAutocomplete?: boolean;
}

/**
 * The event tags component is used to display and edit tags for an event.
 *
 * @param props
 * @returns
 */
export function EventTags(props: Props) {
  const [tags, setTags] = useState<Tags>(props.tags || {});
  const [questions, setQuestions] = useState<Tag[]>([]);
  const canWrite = useHasPermission('events:write:tags');

  const availableTags = useTagsQuery();
  const eventTags = useEventTags(props.event?.id);

  useEffect(() => {
    if (availableTags.data) {
      const activeQuestions = availableTags.data.filter((x: Tag) => x.state === TagState.Active);
      setQuestions(activeQuestions);
    }
  }, [availableTags.data]);

  useEffect(() => {
    // Load default selection of tags, if passed into here
    props.tags && setTags(props.tags);
  }, [props.tags]);

  useEffect(() => {
    // Set default tags selection to whatever is backend response for that event
    if (eventTags.isLoading && !props.tags) {
      setTags({});
    }
    if (eventTags.data) {
      setTags(eventTags.data);
      props.onTagsLoad?.(eventTags.data);
      props.onUserTagChange?.(eventTags.data);
    }
  }, [eventTags.data, eventTags.isLoading]);

  return (
    <div>
      {!props.hideButtons &&
        questions
          .filter((q) => !q.is_rare)
          .map((question) => (
            <div key={question.id}>
              <SpaceBetween>
                <label>{humanizeEnum(question.name)}</label>
                {question.description && <HelpIcon info={question.description} />}
              </SpaceBetween>

              <Spacer half />

              <Question
                question={question}
                onTagClick={handleButtonClick}
                tags={tags}
                setTags={setTags}
                onUserTagChange={props.onUserTagChange}
                disabled={!canWrite}
              />

              <hr />
            </div>
          ))}
      {props.withAutocomplete && (
        <>
          <Autocomplete
            onTagClick={handleDropdownClick}
            questions={questions}
            tags={tags}
            setTags={setTags}
            onUserTagChange={props.onUserTagChange}
            dataTestId="autocompleteTags"
          />
          <FilterToggleWrapper>
            {Object.entries(tags).map(([key, value]) => (
              <ToggleButton
                onClick={handleTagClick(key, tags, setTags, props.onUserTagChange)}
                key={key + value}
                disabled={!canWrite}
              >
                {tagDescription(key, value, questions)}
              </ToggleButton>
            ))}
          </FilterToggleWrapper>
        </>
      )}
    </div>
  );
}
